import React, { forwardRef, useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../form/Button";
import { apiRequest } from "src/async/apiUtils";
import { useParams } from "react-router-dom";
import Modal from "./Modal";

const NotificationBanner = forwardRef(({ data, setActiveNotifications = () => {}, user }, ref) => {
  const [show, setShow] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const { workspace_id } = useParams();
  const [preview, setPreview] = useState("");
  const [isClamped, setIsClamped] = useState(false);

  const contentRef = useRef(null);

  const disMissNotification = async () => {
    setShowPopup(false);
    setShow(false);
    setActiveNotifications(null);
    if (user?._id) {
      try {
        await apiRequest("POST", "/portal/workspaces/:workspace_id/notifications/:notification_id/dismiss", {
          params: { workspace_id, notification_id: data?._id },
        });
      } catch (error) {}
    } else {
      localStorage.setItem(`notification_${data?._id}_dismissed`, data?._id);
    }
  };

  const handleShowMore = () => setShowPopup(true);
  const closePopup = () => setShowPopup(false);

  const checkIfClamped = () => {
    const el = contentRef.current;
    if (!el) return;

    const overflowing = el.scrollWidth > el.clientWidth + 1;
    setIsClamped(overflowing);
  };

  useEffect(() => {
    setShow(true);
    setPreview(data?.content);

    const frame = requestAnimationFrame(() => {
      checkIfClamped();
    });

    return () => cancelAnimationFrame(frame);
  }, [data?.content, data?._id]);

  useEffect(() => {
    const handleResize = () => checkIfClamped();
    window.addEventListener("resize", handleResize);
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        checkIfClamped();
      });
    });
    if (contentRef.current) resizeObserver.observe(contentRef.current);
    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <>
      {show && (
        <div
          // ref={ref}
          style={{ backgroundColor: data?.background_color, color: data?.text_color }}
          className="relative z-[60] flex h-16 flex-shrink-0 items-center px-4">
          <div className="flex w-full items-center px-3">
            <div className="relative flex-grow overflow-hidden">
              <div
                ref={contentRef}
                className="truncate pr-20 text-base font-medium !leading-tight"
                style={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                {preview}
              </div>

              {isClamped && (
                <button
                  type="button"
                  onClick={handleShowMore}
                  className="absolute right-0 top-0 h-full pl-1 text-sm font-semibold leading-none hover:underline"
                  style={{
                    color: data?.text_color,
                  }}>
                  Show more
                </button>
              )}
            </div>

            <div className="ml-auto flex-shrink-0">
              <Button
                buttonHasLink={false}
                buttonFunction={disMissNotification}
                buttonClasses={"!p-0 !h-10 !w-10 !bg-transparent !text-slate-500"}
                buttonLabel={
                  <XMarkIcon
                    className="h-6 w-6"
                    style={{ color: data?.text_color }}
                  />
                }
                buttonLabelClasses={"transition-none"}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={showPopup}
        defaultStyles={{ overFlowYVisible: false }}
        onSuccess={disMissNotification}
        defaultOptions={{
          onCancelButtonVisible: true,
          onCancelButtonText: "Close",
          onSuccessButtonVisible: true,
          onSuccessButtonText: "Got it!",
          wordWrap: true,
          customStyles: {
            backgroundColor: data?.background_color,
            color: data?.text_color,
          },
        }}
        onCancel={closePopup}>
        <div className="overflow-hidden whitespace-pre-wrap break-words">{data?.content}</div>
      </Modal>
    </>
  );
});

export default NotificationBanner;
